import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import CommentList from './CommentList';
import ParentInput from './ParentInput';
import ICON_SORT_CREATE_LIGHT from '../../../public/static/v4/common/icon-sort-create-light.svg';
import ICON_SORT_CREATE from '../../../public/static/v4/common/icon-sort-create.svg';
import ICON_SORT_LIKE_LIGHT from '../../../public/static/v4/common/icon-sort-like-light.svg';
import ICON_SORT_LIKE from '../../../public/static/v4/common/icon-sort-like.svg';
import Button from '../../common/component/Button';
import Header from '../../common/component/Header';
import Modal, {FULL_CONTENT_STYLE} from '../../common/component/Modal';
import PreventMobileBodyScroll from '../../common/component/PreventMobileBodyScroll';
import Text from '../../common/component/Text';
import useTheme from '../../common/hook/useTheme';
import {formatNumberWithMax} from '../../common/util/number';
import MaxWidth from '../../commonSub/component/MaxWidth';
import useCommentEvent from '../hook/useCommentEvent';
import {
  ICommentInputVariant,
  ICommentSort,
  IUseCommentReturnValue,
} from '../type';

interface IProps extends IUseCommentReturnValue {
  isOpen: boolean;
  isOnlyDarkMode?: boolean;
  bgColor?: string;
  inputVariant?: ICommentInputVariant;
  initialSort: ICommentSort;
  useSpoiler?: boolean;
  setSort: (sort: ICommentSort) => void;
  onRequestClose: () => void;
}

export default function CommentModal({
  isOpen,
  isOnlyDarkMode,
  bgColor,
  inputVariant,
  initialSort,
  useSpoiler = true,
  setSort,
  onRequestClose,
  ...data
}: IProps) {
  const {t} = useTranslation();
  const {isLightMode} = useTheme();
  bgColor ??= !isOnlyDarkMode && isLightMode ? '#ffffff' : '#121212';

  const {totalCount, postComment, toggleSort, fetchComments, sort, ...rest} =
    data;

  useEffect(() => {
    if (isOpen) {
      fetchComments(sort, true);
    } else {
      setSort(initialSort);
    }
  }, [fetchComments, initialSort, isOpen, setSort, sort]);

  const requestGetNextList = () => {
    fetchComments(sort);
  };

  const contentStyle = bgColor
    ? {...FULL_CONTENT_STYLE, backgroundColor: bgColor}
    : FULL_CONTENT_STYLE;

  const {sendCommentEvent} = useCommentEvent();
  const onClose = () => {
    sendCommentEvent({actionName: 'back', action: 'click'});
    onRequestClose();
  };

  const onClickSort = () => {
    sendCommentEvent({
      actionName: 'sorting_option',
      action: 'click',
      customProps: {sorting_option: sort === 'createdDt' ? 'best' : 'new'},
    });
    toggleSort();
  };

  const gradientHeight = useSpoiler ? SPOILER_GRADIENT_HEIGHT : GRADIENT_HEIGHT;
  const commentTitle =
    totalCount >= 2 ? t('comment_title_plural') : t('comment_title');
  const countStr = totalCount > 0 ? ` ${formatNumberWithMax(totalCount)}` : '';

  return (
    <Modal contentStyle={contentStyle} isOpen={isOpen} onRequestClose={onClose}>
      <div
        className="h-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <Header
          isOnlyDarkMode={isOnlyDarkMode}
          buttonIcon="back"
          title={
            <div className="absolute inset-0 flex-center">
              <Text
                className={cn(
                  's16-bold-white',
                  !isOnlyDarkMode && 'light:text-black'
                )}
              >
                {`${commentTitle}${countStr}`}
              </Text>
            </div>
          }
          onClick={onClose}
          rightElement={
            rest.ids.length > 0 ? (
              <Button onClick={onClickSort}>
                <img
                  src={
                    sort === 'createdDt'
                      ? !isOnlyDarkMode && isLightMode
                        ? ICON_SORT_CREATE_LIGHT
                        : ICON_SORT_CREATE
                      : !isOnlyDarkMode && isLightMode
                        ? ICON_SORT_LIKE_LIGHT
                        : ICON_SORT_LIKE
                  }
                  alt=""
                />
              </Button>
            ) : undefined
          }
        />
        {/* header 자리를 채우기 위해 넣는다. */}
        <div className="h-default-gradient-header-height" />
        <div
          className="absolute w-full z-2"
          style={{
            height: `${gradientHeight}px`,
          }}
        >
          <div
            className="absolute w-full h-full -z-1"
            style={{
              background: `linear-gradient(to bottom, ${bgColor}, ${bgColor}E6 24%, ${bgColor}B3 50%, ${bgColor}73 75%, ${bgColor}00`,
            }}
          />
          <MaxWidth hasMdWidth>
            <ParentInput
              variant={inputVariant}
              useSpoiler={useSpoiler}
              onClickPost={postComment}
            />
          </MaxWidth>
        </div>
        <PreventMobileBodyScroll className="h-full-minus-gradient-header-height">
          <MaxWidth
            hasMdWidth
            className={cn(
              'px-18',
              'no-scrollbar',
              useSpoiler ? 'mt-64' : 'mt-75'
            )}
            style={{
              marginTop: `${gradientHeight - 24}px`, // 댓글 상단 여백만큼 제외
            }}
          >
            <CommentList requestGetNextList={requestGetNextList} {...rest} />
          </MaxWidth>
        </PreventMobileBodyScroll>
      </div>
    </Modal>
  );
}

const SPOILER_GRADIENT_HEIGHT = 116;
const GRADIENT_HEIGHT = 88;
